import styled, { css, FlattenInterpolation, ThemeProps } from 'styled-components';
import buttonResetStyle from '../../commonStyles/buttonReset';
import { twoLayeredBoxShadow } from '../../commonStyles/styling';
import colors from '../../helpers/colors';
import fonts from '../../helpers/fonts';
import spacing from '../../helpers/spacing';
import { Theme } from '../../theme';

export type ThemeColor = ({ theme }: ThemeProps<Theme>) => string;

/* Microsoft Edge stylesheet hierachy acts differently to other browsers. 
This state styling is included in baseLinkStyle and actionLinkStyle 
where the increased specificity required for Edge causes the base state styles to be overriden. */
const linkStateStyling = css`
  &:hover {
    color: ${colors.core02};
    text-decoration: underline;
  }
  &:focus-visible,
  &:active {
    outline: none;
    text-decoration: none;
  }
  &:focus-visible {
    color: ${colors.core02};
  }
  &:active {
    color: ${colors.neutral02};
  }
  &:disabled {
    color: ${colors.neutral04};
    text-decoration: none;
  }
`;

export const baseLinkStyle = css`
  position: relative;
  text-decoration: none;
  ${linkStateStyling}
`;

export const blockLinkStyle = css`
  ${baseLinkStyle};

  display: block;
  width: fit-content;

  &:focus-visible {
    &:after {
      position: absolute;
      content: '';
      top: -2px;
      bottom: -2px;
      left: -1px;
      right: -1px;
      box-shadow: ${twoLayeredBoxShadow(colors.neutral08, colors.core02)};
      border-radius: 4px;
    }
  }
`;

export const inlineTextLinkStyle = css`
  ${baseLinkStyle};

  &:focus-visible,
  &:active {
    box-shadow: ${twoLayeredBoxShadow(colors.neutral08, colors.core02)};
    border-radius: 4px;
  }
`;

type LinkStyleProps = {
  mainColor?: ThemeColor;
  removeVisitedStyle?: boolean;
};

const removeVisitedStyling = (
  props: LinkStyleProps
): FlattenInterpolation<ThemeProps<Theme> & LinkStyleProps> => css`
  &:link${props.removeVisitedStyle && ', &:visited'} {
    color: ${props.mainColor ?? 'inherit'};
  }
`;

export const StyledLink = styled.a<LinkStyleProps>`
  ${(props) => removeVisitedStyling(props)};

  ${blockLinkStyle}
`;

type ButtonLinkStyleProps = {
  mainColor?: ThemeColor;
  disabled?: boolean;
};

export const ButtonStyledAsLink = styled.button<ButtonLinkStyleProps>`
  ${buttonResetStyle}
  & {
    color: ${(props) => props.mainColor ?? 'inherit'};
    cursor: ${(props) => (props.disabled ? 'auto' : 'pointer')};
    font-weight: inherit;
  }
  ${blockLinkStyle}
`;

export const actionLinkStyle = css`
  ${fonts.paragraph}
  && {
    color: ${colors.core01};
    font-weight: normal;
    text-decoration: underline;
    ${linkStateStyling}
  }

  display: block;
  margin-top: ${spacing(1)};
`;
